/**
 * Pure view helpers for the send plan (`GET /v1/outreach/plan`).
 *
 * Days and times are Berlin's – the owner's clock and the warm-up cap's day
 * boundary. The recipient's own time is shown beside it, because office hours
 * are theirs.
 */
import type { PlanDelay, PlanItem } from "../api/b2g";

const BERLIN = "Europe/Berlin";
const DAY_KEY = new Intl.DateTimeFormat("en-CA", { timeZone: BERLIN, year: "numeric", month: "2-digit", day: "2-digit" });
const DAY_LABEL = new Intl.DateTimeFormat("en-GB", { timeZone: BERLIN, weekday: "short", day: "numeric", month: "short" });
const TIME_FORMATS = new Map<string, Intl.DateTimeFormat>();

function timeFormat(timeZone: string): Intl.DateTimeFormat {
  let f = TIME_FORMATS.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    TIME_FORMATS.set(timeZone, f);
  }
  return f;
}

/** "2026-10-22" – the Berlin calendar day of an instant. */
export function berlinDay(at: string | Date): string {
  return DAY_KEY.format(new Date(at));
}

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** "Today", "Tomorrow", else "Thu 22 Oct". */
export function dayLabel(day: string, today: string): string {
  if (day === today) return "Today";
  if (day === addDays(today, 1)) return "Tomorrow";
  return DAY_LABEL.format(new Date(`${day}T12:00:00Z`));
}

/** "08:03" in Berlin. */
export function berlinTime(at: string): string {
  return timeFormat(BERLIN).format(new Date(at));
}

/** The recipient's wall clock at that instant, or null when it reads like Berlin's. */
export function localTime(at: string, timeZone: string): string | null {
  const local = timeFormat(timeZone).format(new Date(at));
  return local === berlinTime(at) ? null : local;
}

/** "today 11:42", "tomorrow 03:00", else "Thu 22 Oct". */
export function whenLabel(at: string, today: string): string {
  const day = berlinDay(at);
  if (day === today) return `today ${berlinTime(at)}`;
  if (day === addDays(today, 1)) return `tomorrow ${berlinTime(at)}`;
  return dayLabel(day, today);
}

const PREVIOUS = ["", "the first email", "follow-up 1"];

const DELAY_TEXT: Record<PlanDelay, string> = {
  business_hours: "waits for office hours",
  daily_cap: "daily limit full",
  queue: "behind other emails",
};

/** Why an email goes out when it does – the rule behind its date, then what pushed it later. */
export function planReason(item: PlanItem): string {
  const parts: string[] = [];
  switch (item.basis) {
    case "approval":
      parts.push("due since your approval");
      break;
    case "planned":
      parts.push("on the date the seat set");
      break;
    case "follow_up":
      parts.push(`${item.delay_days} days after ${PREVIOUS[item.step] ?? "the step before"}`);
      break;
    case "return_date":
      parts.push("after their out-of-office return");
      break;
    case "retry":
      parts.push("retry after a failed send");
      break;
  }
  for (const d of item.delays) parts.push(DELAY_TEXT[d]);
  if (item.delivery === "drafts") parts.push("lands as a draft – you send");
  return parts.join(" · ");
}

/** Why an email has no time yet. */
export function holdText(item: PlanItem, horizonDays: number): string {
  switch (item.hold) {
    case "your_send":
      return "draft in Outlook – waiting for your send";
    case "after_your_send":
      return `${item.delay_days} days after you send ${PREVIOUS[item.step] ?? "the step before"}`;
    case "wave_paused":
      return "wave paused";
    case "mailbox_paused":
      return "mailbox paused";
    case "sending_disabled":
      return "sending is switched off on the server";
    case "unscheduled":
      return "no send date yet";
    case "beyond_horizon":
      return `more than ${horizonDays} days out`;
    default:
      return "";
  }
}

/** "≈ Thu 22 Oct 08:03 (09:03 local) · 7 days after the first email" – one email's place in the plan. */
export function planLine(item: PlanItem, today: string, horizonDays: number): string {
  if (!item.at) return holdText(item, horizonDays);
  const day = berlinDay(item.at);
  const date = day === today ? "today" : day === addDays(today, 1) ? "tomorrow" : dayLabel(day, today);
  const local = localTime(item.at, item.time_zone);
  return `≈ ${date} ${berlinTime(item.at)}${local ? ` (${local} local)` : ""} · ${planReason(item)}`;
}

export interface PlanDay {
  day: string;
  items: PlanItem[];
}

/** Timed items by Berlin day (in the server's order), and the ones on hold. */
export function groupByDay(items: PlanItem[]): { days: PlanDay[]; held: PlanItem[] } {
  const days: PlanDay[] = [];
  const held: PlanItem[] = [];
  for (const item of items) {
    if (!item.at) {
      held.push(item);
      continue;
    }
    const day = berlinDay(item.at);
    const last = days[days.length - 1];
    if (last && last.day === day) last.items.push(item);
    else days.push({ day, items: [item] });
  }
  return { days, held };
}

/** The days shown open by default: whole days until about `rows` emails are listed, at least one. */
export function defaultOpenDays(days: PlanDay[], rows = 8): Set<string> {
  const open = new Set<string>();
  let shown = 0;
  for (const d of days) {
    if (open.size > 0 && shown + d.items.length > rows) break;
    open.add(d.day);
    shown += d.items.length;
  }
  return open;
}

export interface WavePlan {
  next: PlanItem | null;
  last: PlanItem | null;
  held: PlanItem[];
}

/** Per wave: the next and the last predicted email, and what is on hold. */
export function planByWave(items: PlanItem[]): Map<string, WavePlan> {
  const out = new Map<string, WavePlan>();
  for (const item of items) {
    const w = out.get(item.wave_id) ?? { next: null, last: null, held: [] };
    if (!item.at) w.held.push(item);
    else {
      if (!w.next) w.next = item;
      w.last = item;
    }
    out.set(item.wave_id, w);
  }
  return out;
}

export function planByMessage(items: PlanItem[]): Map<string, PlanItem> {
  return new Map(items.map((i) => [i.message_id, i]));
}

/** What the server still sends today (draft delivery left out – the owner sends those). */
export function restOfToday(items: PlanItem[], today: string): PlanItem[] {
  return items.filter((i) => i.at && i.delivery === "send" && berlinDay(i.at) === today);
}
